import {useState} from 'react'
import {Helmet} from 'react-helmet'
import {Navigate, useLocation} from 'react-router-dom'
import {useTranslation} from '@/translations/i18n.tsx'
import {Container} from '@components/ui/container'
import {Spinner} from '@components/ui/spinner'
import {useFloorPlanOptin} from '@features/floor-plan/queries/useFloorPlanOptin.ts'
import {useFloorPlanOptInDefaults} from '@features/floor-plan/queries/useFloorPlanOptInDefaults.ts'
import {FloorPlanOptInHeader} from '@features/floor-plan/components/floor-plan-opt-in-header'
import {FloorPlanOptInForm} from '@features/floor-plan/components/floor-plan-opt-in-form'
import {FloorPlanCompletedStep} from '@features/floor-plan/components/floor-plan-completed'
import {FloorPlanOptInOrigin} from '@features/floor-plan/components/floor-plan-optin-button'
import {routes} from '@utilities/constants/routes'

export const FloorPlanOptIn = () => {
    const {t} = useTranslation()
    const location = useLocation()
    const from = (location.state as {from?: FloorPlanOptInOrigin} | null)?.from
    const {isOptedIn, isVisible} = useFloorPlanOptin()
    const defaults = useFloorPlanOptInDefaults()
    const [isSubmitted, setIsSubmitted] = useState(false)

    // Already opted in (or just submitted) -> show the completed step directly.
    const showCompleted = isOptedIn || isSubmitted

    // This route lives outside PrivateLayout, so the user isn't pre-loaded.
    // Gate the whole page (the header avatar needs it) until defaults resolve.
    if (!defaults) {
        return (
            <div className="flex min-h-dvh flex-col">
                <div className="flex flex-1 items-center justify-center">
                    <Spinner size={40} />
                </div>
            </div>
        )
    }

    // Floor-plan opt-in is hidden for non-photographers. defaults resolving
    // guarantees the user is loaded, so isVisible is reliable here (no false
    // redirect while useMe is in flight).
    if (!isVisible) {
        return <Navigate to={routes.HOME.handle.fullPath} replace />
    }

    return (
        <div className="flex min-h-dvh flex-col">
            <Helmet title={t('floor_plan:opt_in:meta_title')} />
            <FloorPlanOptInHeader />
            <main className="flex flex-1 flex-col justify-center gap-6 pt-6 pb-12 md:gap-8 md:pt-12 md:pb-16">
                <Container>
                    <div className="flex max-w-180 flex-col gap-3">
                        <h1 className="text-display-sm font-medium text-neutral-900">
                            {showCompleted ? t('floor_plan:capture_modal:title') : t('floor_plan:opt_in:page_title')}
                        </h1>
                        {!showCompleted && (
                            <p className="text-md whitespace-pre-line text-neutral-700">
                                {t('floor_plan:opt_in:page_subtitle')}
                            </p>
                        )}
                    </div>
                </Container>
                <Container>
                    {showCompleted ? (
                        <FloorPlanCompletedStep from={from} />
                    ) : (
                        <FloorPlanOptInForm defaults={defaults} onSuccess={() => setIsSubmitted(true)} />
                    )}
                </Container>
            </main>
        </div>
    )
}
